import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  FormControlLabel,
  FormHelperText,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Grid,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  History as HistoryIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Accordion from '../UIKit/Accordion';
import Button from '../UIKit/Button';
import Switch from '../UIKit/Switch';
import TextField from '../UIKit/TextField';
import { useTranslation } from 'react-i18next';

const BadgeCheckDialog = ({
  open,
  onClose,
  loadingUsers,
  availableUsers,
  selectedUser,
  setSelectedUser,
  dryRun,
  setDryRun,
  checkResult,
  checkLoading,
  checkHistory,
  handleRunCheck,
  formatDateTime,
  error,
}) => {
  const { t } = useTranslation(['badges', 'common']);
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h5"
          sx={{ fontSize: '1.25rem', fontWeight: 600, textAlign: 'center' }}
        >
          {t('badges:check.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1, textAlign: 'center' }}
        >
          {t('badges:check.subtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              {t('badges:check.selectUser')}
            </Typography>
            <Autocomplete
              id="user-select"
              loading={loadingUsers}
              options={availableUsers}
              getOptionLabel={(option) =>
                `${option.fullName} (${option.email})`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('badges:check.user')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingUsers ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  helperText={t('badges:check.userHelper')}
                />
              )}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
              value={selectedUser}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!dryRun}
                  onChange={(e) => setDryRun(!e.target.checked)}
                  sx={{ mx: 1 }}
                />
              }
              label={t('badges:check.realAward')}
            />
            <FormHelperText sx={{ color: 'text.secondary' }}>
              {dryRun
                ? t('badges:check.dryRunHelper')
                : t('badges:check.realAwardHelper')}
            </FormHelperText>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
                {error}
              </Alert>
            </Grid>
          )}

          {checkResult && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  my: 2,
                  color: 'text.primary',
                  borderRadius: '16px',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('badges:check.results')}
                </Typography>

                <Typography variant="body1" gutterBottom>
                  {checkResult.message}
                </Typography>

                {checkResult.totalAwarded > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {checkResult.details
                      .filter((detail) => detail.awarded > 0)
                      .map((detail, idx) => (
                        <Paper
                          key={idx}
                          elevation={0}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: '16px',
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            display: 'flex',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: detail.dryRun
                                ? 'warning.light'
                                : 'success.light',
                              color: 'white',
                              p: 1,
                              borderRadius: '50%',
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {detail.dryRun ? (
                              <AccessTimeIcon fontSize="small" />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </Box>

                          <Box sx={{ flexGrow: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 500, mb: 0.5 }}
                            >
                              {t('badges:check.userLabel', {
                                username: detail.username,
                              })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('badges:check.awardCount', {
                                count: detail.awarded,
                              })}
                              {detail.badges && detail.badges.length > 0 && (
                                <>
                                  {' '}
                                  (
                                  {detail.badges.map((badge, bidx) =>
                                    bidx === 0
                                      ? `"${badge.badgeName}"`
                                      : `, "${badge.badgeName}"`
                                  )}
                                  )
                                </>
                              )}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 1.5,
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: '16px',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <InfoIcon color="info" sx={{ mr: 1.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      {checkResult.dryRun
                        ? t('badges:check.noBadgesFound')
                        : t('badges:check.noBadgesAwarded')}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          {checkHistory.length > 0 && (
            <Grid item xs={12}>
              <Accordion elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    <HistoryIcon
                      fontSize="small"
                      sx={{ verticalAlign: 'middle', mr: 1 }}
                    />
                    {t('badges:check.history')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {checkHistory.map((check, idx) => (
                      <ListItem
                        key={idx}
                        divider={idx < checkHistory.length - 1}
                      >
                        <ListItemIcon>
                          {check.dryRun ? (
                            <AccessTimeIcon color="warning" />
                          ) : (
                            <CheckCircleIcon color="success" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <>
                              {formatDateTime(check.timestamp)}
                              <Chip
                                label={
                                  check.dryRun
                                    ? t('badges:check.checkLabel')
                                    : t('badges:check.awardLabel')
                                }
                                size="small"
                                color={check.dryRun ? 'default' : 'success'}
                                sx={{ ml: 1 }}
                              />
                            </>
                          }
                          secondary={
                            <>
                              {t('badges:check.historyUser', {
                                user:
                                  check.userName ||
                                  (check.userId === 'all'
                                    ? t('common:common.all')
                                    : check.userId),
                              })}
                              {', '}
                              {t('badges:check.historyAwarded', {
                                count: check.result.totalAwarded || 0,
                              })}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'center', gap: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ minWidth: 100 }}>
          {t('common:actions.close')}
        </Button>
        <Button
          onClick={handleRunCheck}
          variant="contained"
          color="primary"
          startIcon={checkLoading ? null : <PlayArrowIcon />}
          disabled={checkLoading}
        >
          {checkLoading ? (
            <CircularProgress size={24} />
          ) : dryRun ? (
            t('badges:check.runCheck')
          ) : (
            t('badges:check.awardBadges')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeCheckDialog;
